import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import {
  CreateServiceResponseResult,
  Option,
  Service,
  ServiceDetails,
  ServiceDetailsResponse,
  ServiceTerms,
} from '@/app/shared-components/Types/Service';
import { Paged } from '../shared-components/Types/Paged';
import { apiSlice } from './apiSlice';
import { OptionsSchema, Schema } from '@/app/main/add-service/components/config';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { createMyUtiliesURL } from '@/utils/helpers';
import {
  CreateEditRateCard,
  CreateRateCardResponse,
  RateCard,
} from '../shared-components/Types/RateCard';
import { ProviderLicensee } from '../shared-components/Types/Provider';
import { AdditionalFilter } from '@/app/hooks/useFilterPagination';
import { ServiceItem } from '../providers/GlobalDataProvider';
import { ProductDetailsResponse } from '../main/order-details/DsiType';

export const servicesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getServiceTypes: builder.query<Paged<ServiceItem>, void>({
      query: () => ({ url: apiRoutes.servicesTypes, method: 'GET' }),
    }),
    getServiceTypesByLicenseeId: builder.query<Paged<ServiceItem>, number | undefined>({
      query: (licenseeId: number | undefined) => {
        const url = licenseeId
          ? `${apiRoutes.servicesTypes}/${licenseeId}`
          : apiRoutes.servicesTypes;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getServices: builder.query<
      Paged<Service>,
      {
        id: string;
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
        pagination: MRT_PaginationState;
      }
    >({
      query: ({ filters, sorting, pagination }) => {
        const urlParams = createMyUtiliesURL(filters, sorting);

        return {
          url: apiRoutes.services,
          method: 'GET',
          params: {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            ...Object.fromEntries(urlParams),
          },
        };
      },
      providesTags: ['Services'],
    }),
    getServicesByProvider: builder.query<
      Paged<Service>,
      {
        id: string;
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
        pagination: MRT_PaginationState;
        extraFilters: AdditionalFilter[];
      }
    >({
      query: ({ id, filters, sorting, pagination, extraFilters }) => {
        const urlParams = createMyUtiliesURL(filters, sorting);
        const extraFiltersParams = new URLSearchParams();
        extraFilters.forEach((filter) => {
          extraFiltersParams.append(filter.name, filter.value as string);
        });

        return {
          url: `${apiRoutes.providers}/${id}/services`,
          method: 'GET',
          params: {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            ...Object.fromEntries(urlParams),
            ...Object.fromEntries(extraFiltersParams),
          },
        };
      },
      providesTags: ['ProviderServices'],
    }),
    getService: builder.query<ServiceDetails, string | undefined>({
      query: (serviceId) => {
        if (serviceId) {
          return { url: `${apiRoutes.services}/${serviceId}`, method: 'GET' };
        }
        throw new Error('Service id is not defined');
      },
      providesTags: (_result, _error, serviceId) => [{ type: 'Service', id: serviceId }, 'Service'],
    }),
    getServiceRateCards: builder.query<RateCard[], string | undefined>({
      query: (serviceId) => {
        if (serviceId) {
          return { url: `${apiRoutes.services}/${serviceId}/ratecards`, method: 'GET' };
        }
        throw new Error('Service id is not defined');
      },
      providesTags: (_result, _error, serviceId) => [{ type: 'RateCards', id: serviceId }],
    }),
    getServiceTerms: builder.query<ServiceTerms, string | undefined>({
      query: (serviceId) => {
        if (serviceId) {
          return { url: `${apiRoutes.services}/${serviceId}/terms`, method: 'GET' };
        }
        throw new Error('Service id is not defined');
      },
      providesTags: (_result, _error, serviceId) => [{ type: 'Service', id: serviceId }],
    }),
    updateServiceTerms: builder.mutation<
      void,
      { serviceId: string | undefined; type: string; term: { term: string } }
    >({
      query: ({ serviceId, type, term }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/terms/${type}`,
            method: 'PUT',
            data: term,
          };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error, { serviceId }) =>
        error ? [] : [{ type: 'Service', id: serviceId }],
    }),
    updateServiceRateCards: builder.mutation<
      RateCard[],
      { serviceId: string | undefined; data: RateCard[] }
    >({
      query: ({ serviceId, data }) => {
        if (serviceId) {
          return { url: `${apiRoutes.services}/${serviceId}/ratecards`, method: 'PUT', data };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error) =>
        error ? [] : ['Services', 'Providers', 'ProviderServices', 'RateCards'],
    }),
    createService: builder.mutation<CreateServiceResponseResult, { id: number; service: Schema }>({
      query: ({ id, service }) => {
        return {
          url: `${apiRoutes.providers}/${id}/service`,
          method: 'POST',
          data: service,
        };
      },
      transformResponse: (_response: CreateServiceResponseResult, meta: string) => {
        return { id: meta };
      },
      invalidatesTags: (_result, error) =>
        error ? [] : ['Services', 'Providers', 'ProviderServices'],
    }),
    updateService: builder.mutation<ServiceDetails, { id: string; service: Schema }>({
      query: ({ id, service }) => {
        return {
          url: `${apiRoutes.services}/${id}`,
          method: 'PUT',
          data: service,
        };
      },
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              'Services',
              'ProviderServices',
              { type: 'Service' as const, id: _result?.id },
              { type: 'RateCards', id: _result?.id },
            ],
    }),
    importServices: builder.mutation<undefined, { data: Record<string, string>[] }>({
      query: ({ data }) => {
        return {
          url: `${apiRoutes.services}/import`,
          method: 'POST',
          data,
        };
      },
      invalidatesTags: (_result, error) =>
        error ? [] : ['Services', 'Providers', 'ProviderServices'],
    }),
    createRateCard: builder.mutation<
      CreateRateCardResponse,
      { serviceId: string | undefined; rateCard: CreateEditRateCard }
    >({
      query: ({ serviceId, rateCard }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/ratecards`,
            method: 'POST',
            data: rateCard,
          };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error) => (error ? [] : ['RateCards']),
    }),
    updateRateCards: builder.mutation<
      CreateRateCardResponse,
      { serviceId: string | undefined; data: RateCard[] }
    >({
      query: ({ serviceId, data }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/ratecards`,
            method: 'PUT',
            data: data,
          };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error) => (error ? [] : ['RateCards']),
    }),
    deleteRateCard: builder.mutation<
      void,
      { serviceId: string | undefined; rateCardId: string | undefined }
    >({
      query: ({ serviceId, rateCardId }) => {
        if (serviceId && rateCardId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/ratecards/${rateCardId}`,
            method: 'DELETE',
          };
        }
        throw new Error('Service/rate card id is not defined');
      },
      invalidatesTags: (_result, error) => (error ? [] : ['RateCards']),
    }),
    // options
    getServiceOptions: builder.query<
      Option[],
      { serviceId: string | number | undefined; id?: string }
    >({
      query: ({ serviceId }) => {
        if (serviceId) {
          return { url: `${apiRoutes.services}/${serviceId}/options`, method: 'GET' };
        }
        throw new Error('Service id is not defined');
      },
      providesTags: ['ServiceOptions'],
    }),
    createServiceOption: builder.mutation<
      Option,
      { serviceId: string | number | undefined; option: OptionsSchema }
    >({
      query: ({ serviceId, option }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/options`,
            method: 'POST',
            data: option,
          };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error) => (error ? [] : ['ServiceOptions']),
    }),
    updateServiceOption: builder.mutation<
      Option,
      { serviceId: string | undefined; optionId: number; option: OptionsSchema }
    >({
      query: ({ serviceId, optionId, option }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/options/${optionId}`,
            method: 'PUT',
            data: option,
          };
        }
        throw new Error('Service id is not defined');
      },
      invalidatesTags: (_result, error) => (error ? [] : ['ServiceOptions']),
    }),
    deleteServiceOption: builder.mutation<
      Option,
      { serviceId: string | undefined; optionId?: number | null }
    >({
      query: ({ serviceId, optionId }) => {
        if (serviceId && optionId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/options/${optionId}`,
            method: 'DELETE',
          };
        }

        throw new Error('Service id is not defined');
      },
      invalidatesTags: ['ServiceOptions'],
    }),
    // details
    getDsiDetails: builder.query<ProductDetailsResponse, { quoteId: number }>({
      query: ({ quoteId }) => {
        if (quoteId) {
          return {
            url: `${apiRoutes.dsi}/ProductDetails/${quoteId}`,
            method: 'GET',
          };
        }
        throw new Error('Service id is not defined');
      },
    }),

    getDetails: builder.query<ServiceDetailsResponse, { serviceId: number }>({
      query: ({ serviceId }) => {
        if (serviceId) {
          return {
            url: `${apiRoutes.services}/${serviceId}/details`,
            method: 'GET',
          };
        }
        throw new Error('Service id is not defined');
      },
    }),

    getDisclosure: builder.query<{ disclosure: string }, { quoteId: number }>({
      query: ({ quoteId }) => {
        if (quoteId) {
          return {
            url: `${apiRoutes.services}/${quoteId}/disclosure`,
            method: 'GET',
          };
        }
        throw new Error('Quote id is not defined');
      },
    }),
    bulkSetStatus: builder.mutation<
      void[],
      { serviceIds: number[]; rateCardDtos: ProviderLicensee[] }
    >({
      query: ({ serviceIds, rateCardDtos }) => {
        return {
          url: `${apiRoutes.services}/bulkupdate/setstatus`,
          method: 'PUT',
          data: { serviceIds, rateCardDtos },
        };
      },
      invalidatesTags: (_result, error, { serviceIds }) =>
        error
          ? []
          : [
              'ProviderServices',
              'Services',
              ...serviceIds.map((serviceId) => ({ type: 'Service' as const, id: serviceId })),
            ],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetServiceTypesQuery,
  useGetServiceTypesByLicenseeIdQuery,
  useGetServicesQuery,
  useGetServiceQuery,
  useGetServiceRateCardsQuery,
  useGetServicesByProviderQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
  useCreateRateCardMutation,
  useDeleteRateCardMutation,
  useUpdateRateCardsMutation,
  useGetServiceOptionsQuery,
  useLazyGetServiceOptionsQuery,
  useCreateServiceOptionMutation,
  useUpdateServiceOptionMutation,
  useLazyGetDetailsQuery,
  useLazyGetDisclosureQuery,
  useLazyGetDsiDetailsQuery,
  useGetDsiDetailsQuery,
  useImportServicesMutation,
  useDeleteServiceOptionMutation,
  useBulkSetStatusMutation,
  useUpdateServiceRateCardsMutation,
  useGetServiceTermsQuery,
  useUpdateServiceTermsMutation,
} = servicesApiSlice;
